//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === COMPONENTS ===
import { Document } from './components/Document';

// === API ===
import { emit, on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

const DocMemo = React.memo(Document);

//-----------------------------------------------------------------
export function Thumbnails(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { fill } = props;

  const theme = useTheme();

  const [documents, setDocuments] = useState({ items: {}, order: [] });
  const [images   , setImages   ] = useState({ items: {}, order: [] });

  const ref_panel = useRef( null );

  let _scroll_throttle = false;

  //#endregion

  //#region [STYLES]

  const _styles = {
    thumb_paper: {
      gridColumn: fill ? `span ${fill}` : 'span 2',
      overflow: 'auto',
      position: 'relative',
      height: '100%',
      width: '100%',
      boxShadow: 0,
      backgroundColor: theme.palette.background.secondary[theme.palette.mode],
    },
    document_container: {
      height: '100%', width: '100%', overflow: 'visible',
    }
  }

  //#endregion

  //#region [EFFECT]

  useEffect(() => {

    //-----------------------------------------------------------------
    function handleDocuments(data) {
      //-----------------------------------------------------------------
      setDocuments(data);
    }

    //-----------------------------------------------------------------
    function handleImages(data) {
      //-----------------------------------------------------------------
      setImages(data);
    }

    //-----------------------------------------------------------------
    function handleImageUpdates(data) {
      //-----------------------------------------------------------------
      if (data.images?.order) {
        setImages(prev => ({
          ...prev,
          order: data.images.order
        }));
      }

      data.images?.update?.forEach(image => {
        emit('IMAGE-' + image.id, image);
      });
    }

    //-----------------------------------------------------------------
    function handleClear(data) {
      //-----------------------------------------------------------------
      setImages({ items: {}, order: [] });
      setDocuments({ items: {}, order: [] });
    }

    on('TASK_DOCUMENTS', handleDocuments);
    on('TASK_IMAGES', handleImages);
    on('IMAGE_UPDATE', handleImageUpdates);
    on('THUMBS_CLEAR', handleClear);

    return () => {
      removeListener('TASK_DOCUMENTS', handleDocuments);
      removeListener('TASK_IMAGES', handleImages);
      removeListener('IMAGE_UPDATE', handleImageUpdates);
      removeListener('THUMBS_CLEAR', handleClear);
    }

  }, []);

  useEffect(() => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData('TASK_VIEW_THUMBS_UNSUBSCRIBE');
    }

    sendMessageData('TASK_VIEW_THUMBS_SUBSCRIBING');

    window.addEventListener('beforeunload', unsubscribe);
    return () => {
      window.removeEventListener('beforeunload', unsubscribe);
    }
  }, []);

  useEffect(() => {
    function _handleReconnect(data) {
      sendMessageData('TASK_VIEW_THUMBS_SUBSCRIBING', data);
    }

    on('connection', _handleReconnect);

    return () => {
      removeListener('connection', _handleReconnect);
    }
  }, []);

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleDocumentUpdates(data) {
      //-----------------------------------------------------------------
      try {
        setDocuments(prev => {
          const temp = {
            items: { ...prev.items },
            order: prev.order
          };

          if (data.documents?.order) {
            temp.order = data.documents.order;
          }

          data.documents?.create?.forEach(doc => {
            temp.items[doc.Id] = doc;
          });

          data.documents?.update?.forEach(doc => {
            temp.items[doc.Id] = {
              ...doc,
              timestamp: Date.now()
            };
          });

          data.documents?.remove?.forEach(id => {
            delete temp.items[id];
          });

          return temp;
        });
      } catch (error) {
        console.warn(error);
      }
    }

    on('DOCUMENT_UPDATE', handleDocumentUpdates);

    return () => {
      removeListener('DOCUMENT_UPDATE', handleDocumentUpdates);
    }
  }, []);
    
  useEffect( () => {
    if( ref_panel?.current == undefined ) return;
    ref_panel.current.addEventListener( 'wheel', onWheel, { passive: false } );

    return () => {
      if( ref_panel.current ) ref_panel.current.removeEventListener( 'wheel', onWheel, { passive: false } );
    }
  }, [ref_panel.current] )

  //#endregion

  //#region [EVENTS]
  
  //-----------------------------------------------------------------
  function onWheel(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
    if (!_scroll_throttle) {
      setTimeout(() => {
        if( event.deltaY > 0 ) {
          sendMessageData( 'TASK_IMAGE_SELECT_NEXT' );
        } else if( event.deltaY < 0 ) {
          sendMessageData( 'TASK_IMAGE_SELECT_PREV' );
        }
        _scroll_throttle = false;
      }, 100);

      _scroll_throttle = true;
    }
  };

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box ref={ref_panel} sx={_styles.thumb_paper}>
        <Box sx={_styles.document_container}>
          {documents.order.map(displayDocument)}
        </Box>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayDocument(id, index) {
    //-----------------------------------------------------------------
    const document = documents.items[id];
    if (!document) return null;
    const timestamp = document?.timestamp;
    return (
      <Box key={'d' + id}>
        <DocMemo
          key={'d' + id}
          id={id}
          timestamp={timestamp}
          index={index}
          images={images.order}
        />
      </Box>
    );
  }

  //#endregion

  return display();
}
